import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectReportingV2Filters,
  selectReportingV2Freshness,
  selectReportingV2Widget,
  setKpiCompareLiveReach,
} from '@/store/slices/reportingV2/reportingV2Slice';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import {
  FREQUENCY_LABEL_LONG,
  currency,
  currencyPrecise,
  decimal,
  isFrequencyComparable,
  num,
  percent,
} from '../format';
import { liveCovers, useDv360LiveReach } from '../hooks/useDv360LiveReach';
import { pctDelta, priorPeriodWindow, useClampedCurrentTotals, usePriorPeriodTotals } from '../hooks/usePriorPeriod';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { COLORS } from '../theme';
import ColumnResizeHandle from './ColumnResizeHandle';
import Delta from './Delta';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (d: string) => {
  const [, m, day] = d.split('-');
  return `${MONTHS[parseInt(m, 10) - 1]} ${parseInt(day, 10)}`;
};

export const ROWS: {
  metric: string;
  label: string;
  format: (v: unknown) => string;
  derive?: (row: Record<string, unknown>) => unknown;
  /** Which direction is good. Omit for 'up'; cost metrics invert it. */
  goodDirection?: 'up' | 'down' | 'neutral';
}[] = [
  { metric: 'impressions', label: 'Impressions', format: num },
  { metric: 'spend', label: 'Spend', format: currency, goodDirection: 'neutral' },
  { metric: 'clicks', label: 'Clicks', format: num },
  {
    metric: 'ctr',
    label: 'CTR',
    format: percent,
  },
  {
    metric: 'cpm',
    label: 'CPM',
    format: currencyPrecise,
    // A falling CPM is buying more efficiently, not a regression.
    goodDirection: 'down',
    derive: (row: Record<string, unknown>) => {
      const imp = Number(row.impressions ?? 0);
      const spd = Number(row.spend ?? 0);
      return imp > 0 ? (spd / imp) * 1000 : null;
    },
  },
  { metric: 'reach', label: 'Reach', format: num },
  {
    metric: 'frequency',
    label: FREQUENCY_LABEL_LONG,
    format: decimal,
    // Rising frequency is neither good nor bad on its own — it's the signal you
    // read against CTR and conversion, so don't colour it as a win or a loss.
    goodDirection: 'neutral',
  },
];

/**
 * Frequency can't be blended across platforms (see isFrequencyComparable), so
 * that row is omitted rather than shown as a plausible wrong number.
 * Spend/CPM (wholesale, #4442) are omitted entirely without "Show wholesale
 * Spend and CPM" (#4636), not rendered as a locked "Restricted" row. Exported
 * standalone so the omission rule is unit-testable without mounting the
 * table (see KpiCompareTable.test.ts).
 */
export const visibleCompareRows = (
  rows: typeof ROWS,
  dsps: string[],
  canViewWholesaleSpend: boolean,
  restrictedFields: string[],
): typeof ROWS =>
  rows.filter(r => {
    if (r.metric === 'frequency') return isFrequencyComparable(dsps);
    if (r.metric === 'spend' || r.metric === 'cpm') {
      return canViewWholesaleSpend && !restrictedFields.includes(`metrics.${r.metric}`);
    }
    return true;
  });

/** Side-by-side current vs prior period comparison table. */
const KpiCompareTable = () => {
  const filters = useAppSelector(selectReportingV2Filters);
  const freshness = useAppSelector(selectReportingV2Freshness);
  const { rows, loading, error, restrictedFields } = useAppSelector(selectReportingV2Widget('totals'));
  const prior = usePriorPeriodTotals();
  // Freshness-clamped pair for the delta only (flank#4718) — the "current"
  // column below keeps showing `totals` (raw, unclamped filter dates); only
  // the comparison needs the two windows to be equal length.
  const comparisonCurrent = useClampedCurrentTotals();
  const { canViewWholesaleSpend } = useReportingPermissions();
  const { widths: colWidths, startResize } = useResizableColumns('kpiCompareTable');

  const visibleRows = visibleCompareRows(ROWS, filters.dsps, canViewWholesaleSpend, restrictedFields);

  const totals = rows[0] ?? {};
  const priorTotals = prior.rows[0] ?? {};
  const comparisonTotals = comparisonCurrent.rows[0] ?? {};
  // Same clamp as the fetch itself, so the labelled prior-period range always
  // matches the dates `prior.rows` actually came from (it previously showed
  // the raw, unclamped shiftBack range even when the fetch below it clamped
  // shorter).
  const { start: priorStart, end: priorEnd } = priorPeriodWindow(
    filters.dateStart,
    filters.dateEnd,
    filters.dsps,
    freshness,
  );

  // DV360-only views read Reach/Frequency live from DV360 (period-deduplicated), since the
  // summed canonical reach overstates it (flank#4479). The hook already ends the current window
  // at DV360's freshness, the same clamp as the comparison window, so one request serves both.
  const liveCurrent = useDv360LiveReach('total');
  const livePrior = useDv360LiveReach('total', { dates: { start: priorStart, end: priorEnd } });
  const liveBusy = liveCurrent.loading || livePrior.loading;
  const liveTotal = (live: typeof liveCurrent, metric: string, urTotals: Record<string, unknown>) => {
    const total = live.byKey.get(null);
    return total && liveCovers(urTotals.impressions, total) ? total[metric as 'reach' | 'frequency'] : null;
  };
  const liveNote = (live: typeof liveCurrent, urTotals: Record<string, unknown>) => {
    const total = live.byKey.get(null);
    if (live.failed) return live.notes.failed;
    if (!liveCovers(urTotals.impressions, total)) return live.notes.partial;
    return total?.reach == null ? live.notes.withheld : live.notes.note;
  };

  const dispatch = useAppDispatch();
  // The page-level Export dialog's Key Metrics section reads the `totals` widget
  // directly — give it the same current-period live reach/frequency this table
  // shows (plus the loading flag, so an export mid-fetch waits instead of
  // silently getting the pre-live summed reach — CodeRabbit, flank#5188), or
  // it silently exports the pre-live summed canonical reach (CodeRabbit /
  // preprod QA, flank#4479).
  //
  // Depends on liveCurrent's own fields (byKey is a stable reference until a
  // new poll actually resolves), not the whole `liveCurrent`/`totals` objects
  // — those are new literals every render, which re-sent to the store on
  // every render for no reason (CodeRabbit, flank#5188).
  useEffect(() => {
    const reach = liveTotal(liveCurrent, 'reach', totals as Record<string, unknown>) as number | null;
    const frequency = liveTotal(liveCurrent, 'frequency', totals as Record<string, unknown>) as number | null;
    dispatch(
      setKpiCompareLiveReach({
        // null, not {reach: null, frequency: null} — an empty pair otherwise
        // flagged the export as "live" for nothing (CodeRabbit, flank#5188).
        value: liveCurrent.active && !liveBusy && (reach != null || frequency != null) ? { reach, frequency } : null,
        loading: liveCurrent.active && liveBusy,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, liveCurrent.active, liveCurrent.loading, liveCurrent.byKey, livePrior.loading, totals.impressions]);
  useEffect(() => () => void dispatch(setKpiCompareLiveReach({ value: null, loading: false })), [dispatch]);

  const currentLabel = `${fmt(filters.dateStart)} – ${fmt(filters.dateEnd)}`;
  const priorLabel = `${fmt(priorStart)} – ${fmt(priorEnd)}`;
  const busy = loading || prior.loading || comparisonCurrent.loading;
  const fail = error || prior.error || comparisonCurrent.error;

  return (
    <Paper variant='outlined' sx={{ borderRadius: '12px', overflow: 'hidden' }}>
      {fail ? (
        <Box sx={{ p: 2 }}>
          <Typography variant='body2' color='error'>
            {fail}
          </Typography>
        </Box>
      ) : (
        <Box className='rv2-scroll' sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ bgcolor: COLORS.pageBg }}>
                <TableCell
                  sx={{
                    position: 'relative',
                    width: colWidths.metric ? `${colWidths.metric}px` : '28%',
                    fontWeight: 700,
                    color: COLORS.muted,
                    fontSize: 12,
                  }}
                >
                  Metric
                  <ColumnResizeHandle columnKey='metric' onResizeStart={startResize} />
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    position: 'relative',
                    width: colWidths.current ? `${colWidths.current}px` : '24%',
                    fontWeight: 700,
                    color: COLORS.muted,
                    fontSize: 12,
                  }}
                >
                  {currentLabel}
                  <ColumnResizeHandle columnKey='current' onResizeStart={startResize} />
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    position: 'relative',
                    width: colWidths.prior ? `${colWidths.prior}px` : '24%',
                    fontWeight: 700,
                    color: COLORS.muted,
                    fontSize: 12,
                  }}
                >
                  {priorLabel}
                  <ColumnResizeHandle columnKey='prior' onResizeStart={startResize} />
                </TableCell>
                <TableCell
                  align='right'
                  sx={{
                    position: 'relative',
                    width: colWidths.change ? `${colWidths.change}px` : '24%',
                    fontWeight: 700,
                    color: COLORS.muted,
                    fontSize: 12,
                  }}
                >
                  Change
                  <ColumnResizeHandle columnKey='change' onResizeStart={startResize} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map(row => {
                const live = liveCurrent.active && (row.metric === 'reach' || row.metric === 'frequency');
                if (busy || (live && liveBusy)) {
                  return (
                    <TableRow key={row.metric}>
                      <TableCell>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {row.label}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>
                        <Skeleton width={64} sx={{ ml: 'auto' }} />
                      </TableCell>
                      <TableCell align='right'>
                        <Skeleton width={64} sx={{ ml: 'auto' }} />
                      </TableCell>
                      <TableCell align='right'>
                        <Skeleton width={48} sx={{ ml: 'auto' }} />
                      </TableCell>
                    </TableRow>
                  );
                }

                const rawCurrent = live
                  ? liveTotal(liveCurrent, row.metric, totals as Record<string, unknown>)
                  : row.derive
                    ? row.derive(totals as Record<string, unknown>)
                    : totals[row.metric];
                const rawComparisonCurrent = live
                  ? liveTotal(liveCurrent, row.metric, comparisonTotals as Record<string, unknown>)
                  : row.derive
                    ? row.derive(comparisonTotals as Record<string, unknown>)
                    : comparisonTotals[row.metric];
                const rawPrior = live
                  ? liveTotal(livePrior, row.metric, priorTotals as Record<string, unknown>)
                  : row.derive
                    ? row.derive(priorTotals as Record<string, unknown>)
                    : priorTotals[row.metric];
                const currentFmt = row.format(rawCurrent);
                const priorFmt = row.format(rawPrior);
                // A missing live value is unknown, not zero; pctDelta would read it as −100%.
                const delta =
                  live && (rawComparisonCurrent == null || rawPrior == null)
                    ? null
                    : pctDelta(rawComparisonCurrent, rawPrior);

                return (
                  <TableRow key={row.metric} sx={{ '&:hover': { bgcolor: COLORS.pageBg } }}>
                    <TableCell>
                      <Tooltip
                        title={live ? liveNote(liveCurrent, totals as Record<string, unknown>) : ''}
                        placement='top'
                        arrow
                      >
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {live && row.metric === 'frequency' ? 'Frequency' : row.label}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {currentFmt}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Typography variant='body2' sx={{ color: COLORS.muted }}>
                        {priorFmt}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {delta !== null ? (
                          <Delta value={delta} goodDirection={row.goodDirection} />
                        ) : (
                          <Typography variant='caption' sx={{ color: COLORS.muted }}>
                            —
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};

export default KpiCompareTable;
